import { Mark, mergeAttributes, getMarkRange } from '@tiptap/core';
import type { EditorState } from '@tiptap/pm/state';

export interface RedlineMarkOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    redline: {
      /** Replace the current selection with `newText` and wrap it in a redline mark
       * carrying the original selection as `oldText`. The caret is placed after
       * the inserted redline so the user can continue editing immediately. */
      setRedline: (oldText: string, newText: string) => ReturnType;
      /** Accept the redline that contains the caret/selection: drop the mark and
       * keep `newText`. */
      acceptRedline: () => ReturnType;
      /** Reject the redline that contains the caret/selection: replace the marked
       * range with the stored `oldText` and drop the mark. */
      rejectRedline: () => ReturnType;
    };
  }
}

const REDLINE_OLD_ATTR = 'data-redline-old';

/**
 * Inline redline diff as a TipTap Mark. Renders the proposed (new) text wrapped
 * in `<span class="ed-redline-new" data-redline-old="...">`. The struck-out
 * original text is rendered via a CSS `::before` pseudo-element from the
 * `data-redline-old` attribute, so the mark composes naturally with paragraph
 * text and round-trips through getHTML/setContent.
 */
export const RedlineMark = Mark.create<RedlineMarkOptions>({
  name: 'redline',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  inclusive: false,
  exitable: true,

  addAttributes() {
    return {
      oldText: {
        default: '',
        parseHTML: (element) => element.getAttribute(REDLINE_OLD_ATTR) ?? '',
        renderHTML: (attributes) => {
          const value = (attributes.oldText as string | undefined) ?? '';
          if (!value) return {};
          return { [REDLINE_OLD_ATTR]: value };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `span.ed-redline-new[${REDLINE_OLD_ATTR}]`,
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        class: 'ed-redline-new',
        'data-redline': 'pending',
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setRedline:
        (oldText, newText) =>
        ({ chain }) => {
          return chain()
            .insertContent({
              type: 'text',
              text: newText,
              marks: [{ type: this.name, attrs: { oldText } }],
            })
            .run();
        },

      acceptRedline:
        () =>
        ({ state, chain }) => {
          const range = findRedlineRange(state);
          if (!range) return false;
          return chain()
            .setTextSelection({ from: range.from, to: range.to })
            .unsetMark(this.name)
            .setTextSelection(range.to)
            .run();
        },

      rejectRedline:
        () =>
        ({ state, chain }) => {
          const range = findRedlineRange(state);
          if (!range) return false;
          const oldText = (range.attrs.oldText as string) ?? '';
          return chain()
            .insertContentAt(
              { from: range.from, to: range.to },
              oldText ? { type: 'text', text: oldText } : '',
            )
            .setTextSelection(range.from + oldText.length)
            .run();
        },
    };
  },
});

function findRedlineRange(state: EditorState): { from: number; to: number; attrs: Record<string, unknown> } | null {
  const markType = state.schema.marks.redline;
  if (!markType) return null;

  const { from } = state.selection;
  const $from = state.doc.resolve(from);
  let range = getMarkRange($from, markType);
  if (!range) {
    const $to = state.doc.resolve(state.selection.to);
    range = getMarkRange($to, markType);
  }
  if (!range) return null;

  const node = state.doc.nodeAt(range.from);
  const mark = node?.marks.find((m) => m.type === markType);
  return { from: range.from, to: range.to, attrs: mark?.attrs ?? {} };
}

export default RedlineMark;
